import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import en from "@/i18n/en.json";
import es from "@/i18n/es.json";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setFormatLocale } from "@/lib/i18nFormat";

type Dict = Record<string, string>;
const DICTS: Record<string, Dict> = { en, es };
const FALLBACK = "en";
const STORAGE_KEY = "sc_locale";

interface I18nCtx {
  locale: string;
  supported: string[];
  setLocale: (l: string) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const Ctx = createContext<I18nCtx | null>(null);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const supported = useMemo(() => user?.supported_locales || ["en", "es"], [user?.supported_locales]);
  const [locale, setLocaleState] = useState<string>(() => localStorage.getItem(STORAGE_KEY) || FALLBACK);

  // Adopt the authenticated user's saved preference on login (unless the user already picked one locally this session).
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored && user?.preferred_locale) setLocaleState(user.preferred_locale);
  }, [user?.preferred_locale]);

  const setLocale = useCallback((l: string) => {
    const next = DICTS[l] ? l : FALLBACK; // honest: unknown locale falls back to English
    setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next);
    document.documentElement.setAttribute("lang", next);
    if (user) api.put("/auth/me/preferences", { preferred_locale: next }).catch(() => {});
  }, [user]);

  useEffect(() => { document.documentElement.setAttribute("lang", locale); setFormatLocale(locale); }, [locale]);

  const t = useCallback((key: string, vars?: Record<string, string | number>) => {
    const dict = DICTS[locale] || DICTS[FALLBACK];
    let s = dict[key] ?? DICTS[FALLBACK][key] ?? key; // locale -> English -> raw key (never a fabricated translation)
    if (vars) for (const k of Object.keys(vars)) s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k]));
    return s;
  }, [locale]);

  const value = useMemo(() => ({ locale, supported, setLocale, t }), [locale, supported, setLocale, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useI18n = (): I18nCtx => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useI18n must be used within I18nProvider");
  return c;
};
